import config from 'constants/config'
import { ITEM_STATUS_AWAITING_DATES, ITEM_STATUS_CANCELLED, ITEM_STATUS_CREATED, ITEM_STATUS_COMPLETED } from 'constants/cart'
import { OFFER_TYPE_HOTEL, OFFER_TYPE_TOUR } from 'constants/offer'
import moment from 'moment'
import { pluralizeToString } from 'lib/string/pluralize'
import { getLatestCompletedReservation } from 'lib/order/orderUtils'
import { isItemUpcoming } from 'lib/order/isUpcoming'

export const canAddFlights = (order: App.Order, geo: App.GeoState, offerFlightsEnabled: boolean) => {
  if (!config.BNBL_FLIGHT_BOOKING_ENABLED) return false
  if (!offerFlightsEnabled) return false
  if (!geo.currentRegionHasFlights) return false

  const alreadyHasFlights = order.flightItems.some(
    ({ status }) => status !== ITEM_STATUS_CANCELLED,
  )
  if (alreadyHasFlights) return false

  const latestReservation = getLatestCompletedReservation(order)
  if (latestReservation && !isItemUpcoming(latestReservation)) return false

  if (order.items?.some(i => i.canBookDates)) return false

  const validStatuses = [
    ITEM_STATUS_AWAITING_DATES,
    ITEM_STATUS_CREATED,
    ITEM_STATUS_COMPLETED,
  ]
  const validAccomItems = order.items.filter(({ status }) => validStatuses.includes(status))
  return (validAccomItems.length === 1)
}

export const changeDatesUntilStr = (checkInDate: string, changeDatesLimit: number) => {
  const formattedDate = moment.utc(checkInDate).subtract(changeDatesLimit, 'day').format('ll')
  return `${formattedDate} (${pluralizeToString('day', changeDatesLimit)} before check-in).`
}

export const reservationCanChangeDates = (reservation: App.OrderReservation, changeDatesLimit: number) => {
  if (!config.accommodation.allowDateChangeOnOrder) return false
  if (!changeDatesLimit || !reservation) return false
  const checkInDate = reservation.startDate
  const finalDateAllowed = moment(checkInDate).subtract(changeDatesLimit, 'days')
  return moment().startOf('day').isSameOrBefore(finalDateAllowed)
}

export const canShowBookByDates = (canBookDates: boolean, bookByDate: string) => {
  if (!canBookDates) return false
  return moment.utc(bookByDate).subtract(2, 'month').isAfter(moment.utc('2021-01-01'))
}

export const getCapacityPath = (item: App.OrderItem, order: App.Order, originAirportCode?: string) => ({
  orderItemId: item.id,
  orderId: order.id,
  originAirportCode,
})

export const hasActiveReservation = (item: App.OrderItem) => {
  if (item.reservationError) return false
  if (!item.reservation) return false
  if (item.status === 'cancelled') return false
  return true
}

export const isDateChangeAllowed = (
  confirmation: boolean,
  item: App.OrderItem,
  reservation: App.OrderReservation,
  isCancelled: boolean,
  hasFlights: boolean,
  numberOfDaysBeforeBookingIsLockedIn: number,
  changeDatesLimitReached: boolean,
  depositDetails: App.Deposit | undefined,
  instalmentDetails: App.InstalmentDetails | undefined,
  reserveForZeroDetails: App.ReserveForZeroDetails | undefined,
) => {
  if (item.offer.type !== OFFER_TYPE_HOTEL) return false
  if (confirmation) return false
  if (!reservation) return false
  if (item.canBookDates) return false
  if (isCancelled) return false
  if (hasFlights) return false
  if (changeDatesLimitReached) return false
  if (depositDetails && ![OFFER_TYPE_HOTEL, OFFER_TYPE_TOUR].includes(item.offer.type)) return false
  if (instalmentDetails && ![OFFER_TYPE_HOTEL].includes(item.offer.type)) return false
  if (reserveForZeroDetails?.is_active) return false
  return reservationCanChangeDates(reservation, numberOfDaysBeforeBookingIsLockedIn)
}
